import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';

// ─── Types ────────────────────────────────────────────────────────────────────
interface ContainerChecklistItem {
  title?: string | null;
  description?: string | null;
  status?: string | null;
  remarks?: string | null;
  evidence_urls?: string[];
}

interface ContainerPhotoItem {
  image_url?: string | null;
  label?: string | null;
  is_required?: boolean;
}

interface ContainerVerificationItem {
  title?: string | null;
  status?: string | null;
  remarks?: string | null;
  evidence_urls?: string[];
}

interface ContainerBundleItem {
  bundle_index?: number | null;
  is_checked?: boolean;
}

export interface ContainerReportPDFData {
  id: string;
  container_no?: string | null;
  buyer_name?: string | null;
  po_number?: string | null;
  vehicle_no?: string | null;
  seal_no?: string | null;
  invoice_no?: string | null;
  shipping_line?: string | null;
  bundle_quantity?: number | null;
  created_at?: string | null;
  status?: string | null;
  remarks?: string | null;
  inspector_signature_url?: string | null;
  factory_rep_signature_url?: string | null;
  inspector_name?: string | null;
  checklists?: ContainerChecklistItem[];
  photos?: ContainerPhotoItem[];
  verifications?: ContainerVerificationItem[];
  bundles?: ContainerBundleItem[];
  // computed overall status injected from detail page
  overall_result?: string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const dash = (v: any): string => {
  if (v === null || v === undefined) return '—';
  const str = String(v).trim();
  return str.length === 0 ? '—' : str;
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 36,
    paddingTop: 20,
    paddingBottom: 64,
  },

  topHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  logoText: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#2563eb' },
  titleArea: { alignItems: 'flex-end' },
  titleLine1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#000000' },
  titleLine2: { fontSize: 9, color: '#555555' },
  hr: { borderBottomWidth: 1, borderBottomColor: '#000000', marginBottom: 10 },

  resultRow: { flexDirection: 'row', borderWidth: 1, borderColor: '#000000', marginBottom: 12 },
  resultBox: { paddingHorizontal: 12, paddingVertical: 8, borderRightWidth: 1, borderRightColor: '#aaaaaa', justifyContent: 'center', minWidth: 130 },
  resultLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2 },
  resultValue: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#000000' },
  statBox: { flex: 1, paddingVertical: 8, paddingHorizontal: 6, borderRightWidth: 0.5, borderRightColor: '#cccccc', alignItems: 'center', justifyContent: 'center' },
  statLabel: { fontSize: 7.5, color: '#666666', textAlign: 'center', marginBottom: 2 },
  statValue: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'center' },

  sectionHeader: { backgroundColor: '#d9d9d9', paddingHorizontal: 8, paddingVertical: 5, marginBottom: 6, marginTop: 4 },
  sectionHeaderText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#000000' },

  subHeader: { backgroundColor: '#f0f0f0', paddingHorizontal: 6, paddingVertical: 3, marginBottom: 4, marginTop: 6 },
  subHeaderText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#333333' },

  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 },
  infoCell: { width: '25%', paddingRight: 8, marginBottom: 8 },
  infoCellHalf: { width: '50%', paddingRight: 8, marginBottom: 8 },
  infoLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2, textTransform: 'uppercase' },
  infoValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#000000' },

  table: { borderWidth: 0.5, borderColor: '#cccccc', marginBottom: 8 },
  tableHead: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 0.5, borderBottomColor: '#cccccc' },
  tableRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: '#eeeeee' },
  th: { flex: 1, fontSize: 8, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 3 },
  td: { flex: 1, fontSize: 8.5, paddingHorizontal: 6, paddingVertical: 3 },

  checklistCard: { borderWidth: 0.5, borderColor: '#cccccc', padding: 6, marginBottom: 5, backgroundColor: '#fafafa' },
  checklistCardHeader: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 3 },
  checklistCardTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#111111', flex: 1 },
  statusBadge: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', paddingHorizontal: 5, paddingVertical: 1, borderWidth: 0.5 },
  checklistDesc: { fontSize: 7.5, color: '#555555', marginBottom: 3 },
  checklistRemark: { fontSize: 8, color: '#333333', fontStyle: 'italic' },
  evidenceThumb: { width: 60, height: 45, objectFit: 'cover', marginTop: 3 },

  bundleStatRow: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  bundleStatBox: { flex: 1, borderWidth: 0.5, borderColor: '#cccccc', padding: 8, alignItems: 'center', backgroundColor: '#fafafa' },
  bundleStatLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#666666', textTransform: 'uppercase', marginBottom: 2 },
  bundleStatValue: { fontSize: 18, fontFamily: 'Helvetica-Bold', color: '#000000' },

  bundleGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 3 },
  bundleCell: { width: 20, height: 20, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5 },
  bundleCellText: { fontSize: 6, fontFamily: 'Helvetica-Bold' },

  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  photoTile: { width: 120 },
  photoLabel: { fontSize: 7.5, color: '#333333', marginBottom: 3 },
  photoImg: { width: 120, height: 90, objectFit: 'cover' },

  remarksBlock: { borderWidth: 0.5, borderColor: '#cccccc', padding: 8, marginBottom: 8, backgroundColor: '#fafafa' },
  remarksLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#555555', marginBottom: 4, textTransform: 'uppercase' },
  remarksText: { fontSize: 9, color: '#222222', lineHeight: 1.4 },

  sigRow: { flexDirection: 'row', gap: 20, marginTop: 16 },
  sigBox: { flex: 1, borderTopWidth: 1, borderTopColor: '#000000', paddingTop: 6 },
  sigLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: '#444444' },
  sigImg: { width: 110, height: 44, objectFit: 'contain', marginBottom: 4 },
  sigName: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  sigRole: { fontSize: 8, color: '#666666' },
  sigEmpty: { height: 44, marginBottom: 4 },

  noDataText: { fontSize: 8, color: '#888888', fontStyle: 'italic', paddingHorizontal: 4, paddingBottom: 6 },

  pageFooter: {
    position: 'absolute', bottom: 14, left: 36, right: 36,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
  },
  footerSide: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Shared sub-components ────────────────────────────────────────────────────
const PageTop = () => (
  <>
    <View style={s.topHeader} fixed>
      <Text style={s.logoText}>QA MATE</Text>
      <View style={s.titleArea}>
        <Text style={s.titleLine1}>CONTAINER INSPECTION REPORT</Text>
        <Text style={s.titleLine2}>QUALITY ASSURANCE DEPARTMENT</Text>
      </View>
    </View>
    <View style={s.hr} fixed />
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerSide} />
    <Text style={s.footerCenter} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
    <Text style={s.footerRight}>QAMATE</Text>
  </View>
);

const SectionBar = ({ title }: { title: string }) => (
  <View style={s.sectionHeader}>
    <Text style={s.sectionHeaderText}>{title}</Text>
  </View>
);

const SubBar = ({ title }: { title: string }) => (
  <View style={s.subHeader}>
    <Text style={s.subHeaderText}>{title}</Text>
  </View>
);

const InfoCell = ({ label, value, half }: { label: string; value: string; half?: boolean }) => (
  <View style={half ? s.infoCellHalf : s.infoCell}>
    <Text style={s.infoLabel}>{label}</Text>
    <Text style={s.infoValue}>{value}</Text>
  </View>
);

// ─── Main PDF Document ────────────────────────────────────────────────────────
const ContainerInspectionReportPDF = ({ inspection }: { inspection: ContainerReportPDFData }) => {
  const checklists = inspection.checklists || [];
  const verifications = inspection.verifications || [];
  const bundles = inspection.bundles || [];
  const photos = inspection.photos || [];

  const passCount = checklists.filter(c => (c.status || '').toLowerCase() === 'pass').length;
  const bundlesChecked = bundles.filter(b => b.is_checked === true).length;
  const bundlesShutout = bundles.filter(b => b.is_checked === false).length;

  const overallResult = inspection.overall_result || (
    inspection.status !== 'COMPLETED' ? 'ACTIVE' :
    (checklists.some(c => (c.status || '').toLowerCase() === 'fail') ||
     verifications.some(v => (v.status || '').toLowerCase() === 'fail')) ? 'FAIL' : 'PASS'
  );

  const stuffingPhotos = photos.filter(p => p.is_required !== false && p.image_url);
  const additionalPhotos = photos.filter(p => p.is_required === false && p.image_url);

  const reportId = `QCI ${inspection.id.slice(0, 6).toUpperCase()}`;

  return (
    <Document title={`Container Inspection Report - ${reportId}`}>

      {/* ═══ PAGE 1 — Result + Summary Details ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        <View style={s.resultRow}>
          <View style={s.resultBox}>
            <Text style={s.resultLabel}>CONTAINER INSPECTION RESULT</Text>
            <Text style={s.resultValue}>{overallResult}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Checklist Score</Text>
            <Text style={s.statValue}>{passCount}/{checklists.length}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Bundles Checked</Text>
            <Text style={s.statValue}>{bundlesChecked}/{dash(inspection.bundle_quantity)}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Shutout</Text>
            <Text style={s.statValue}>{bundlesShutout}</Text>
          </View>
          <View style={[s.statBox, { borderRightWidth: 0 }]}>
            <Text style={s.statLabel}>Report ID</Text>
            <Text style={s.statValue}>{reportId}</Text>
          </View>
        </View>

        <SectionBar title="SUMMARY DETAILS" />
        <View style={s.infoGrid}>
          <InfoCell label="Container No." value={dash(inspection.container_no)} />
          <InfoCell label="Buyer Name" value={dash(inspection.buyer_name)} />
          <InfoCell label="PO Number" value={dash(inspection.po_number)} />
          <InfoCell label="Vehicle No." value={dash(inspection.vehicle_no)} />
          <InfoCell label="Sealing No." value={dash(inspection.seal_no)} />
          <InfoCell label="Invoice Number" value={dash(inspection.invoice_no)} />
          <InfoCell label="Shipping Line" value={dash(inspection.shipping_line)} />
          <InfoCell label="Bundle Quantity" value={inspection.bundle_quantity != null ? `${inspection.bundle_quantity} Bundles` : '—'} />
          <InfoCell label="Inspector" value={dash(inspection.inspector_name)} />
          <InfoCell label="Date" value={formatDate(inspection.created_at)} />
          <InfoCell label="Status" value={dash(inspection.status)} />
        </View>

        <PageFooter />
      </Page>

      {/* ═══ PAGE 2 — 17-Point Checklist ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />
        <SectionBar title={`17-POINT CHECKLIST (Score: ${passCount}/${checklists.length})`} />
        {checklists.length === 0 && <Text style={s.noDataText}>No checklist items recorded.</Text>}
        {checklists.map((item, idx) => {
          const st = (item.status || '').toLowerCase();
          const isPass = st === 'pass';
          const isFail = st === 'fail';
          const color = isPass ? '#16a34a' : isFail ? '#dc2626' : '#64748b';
          const evidenceImgs = (item.evidence_urls || []).filter(Boolean);
          return (
            <View key={idx} style={s.checklistCard} wrap={false}>
              <View style={s.checklistCardHeader}>
                <Text style={s.checklistCardTitle}>{item.title || '—'}</Text>
                <Text style={[s.statusBadge, { color, borderColor: color }]}>{isPass ? 'PASS' : isFail ? 'FAIL' : 'SKIP'}</Text>
              </View>
              {item.description ? <Text style={s.checklistDesc}>{item.description}</Text> : null}
              {item.remarks ? <Text style={s.checklistRemark}>Remark: "{item.remarks}"</Text> : null}
              {evidenceImgs.length > 0 && (
                <View style={{ flexDirection: 'row', gap: 4, marginTop: 3 }}>
                  {evidenceImgs.slice(0, 3).map((url, i) => (
                    <Image key={i} src={url} style={s.evidenceThumb} />
                  ))}
                </View>
              )}
            </View>
          );
        })}
        <PageFooter />
      </Page>

      {/* ═══ PAGE 3 — Final Verification ═══ */}
      {verifications.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="FINAL VERIFICATION" />
          {verifications.map((item, idx) => {
            const st = (item.status || '').toLowerCase();
            const isPass = st === 'pass';
            const isFail = st === 'fail';
            const color = isPass ? '#16a34a' : isFail ? '#dc2626' : '#64748b';
            const evidenceImgs = (item.evidence_urls || []).filter(Boolean);
            return (
              <View key={idx} style={s.checklistCard} wrap={false}>
                <View style={s.checklistCardHeader}>
                  <Text style={s.checklistCardTitle}>{item.title || '—'}</Text>
                  <Text style={[s.statusBadge, { color, borderColor: color }]}>{isPass ? 'PASS' : isFail ? 'FAIL' : 'N/A'}</Text>
                </View>
                {item.remarks ? <Text style={s.checklistRemark}>Inspector Remark: "{item.remarks}"</Text> : null}
                {evidenceImgs.length > 0 && (
                  <View style={{ flexDirection: 'row', gap: 4, marginTop: 3 }}>
                    {evidenceImgs.slice(0, 3).map((url, i) => (
                      <Image key={i} src={url} style={s.evidenceThumb} />
                    ))}
                  </View>
                )}
              </View>
            );
          })}
          <PageFooter />
        </Page>
      )}

      {/* ═══ PAGE 4 — Bundle Summary ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />
        <SectionBar title="BUNDLE CHECKOUT REPORT" />
        <View style={s.bundleStatRow}>
          <View style={s.bundleStatBox}>
            <Text style={s.bundleStatLabel}>Total Bundles</Text>
            <Text style={s.bundleStatValue}>{inspection.bundle_quantity ?? 0}</Text>
          </View>
          <View style={[s.bundleStatBox, { borderColor: '#16a34a' }]}>
            <Text style={[s.bundleStatLabel, { color: '#16a34a' }]}>Checked</Text>
            <Text style={[s.bundleStatValue, { color: '#16a34a' }]}>{bundlesChecked}</Text>
          </View>
          <View style={[s.bundleStatBox, { borderColor: '#dc2626' }]}>
            <Text style={[s.bundleStatLabel, { color: '#dc2626' }]}>Shutout</Text>
            <Text style={[s.bundleStatValue, { color: '#dc2626' }]}>{bundlesShutout}</Text>
          </View>
        </View>

        <SubBar title="BUNDLE GRID VIEW" />
        {bundles.length > 0 ? (
          <View style={s.bundleGrid}>
            {bundles.map((b, i) => {
              const checked = b.is_checked === true;
              return (
                <View key={i} style={[s.bundleCell, { backgroundColor: checked ? '#16a34a' : '#fca5a5', borderColor: checked ? '#15803d' : '#f87171' }]}>
                  <Text style={[s.bundleCellText, { color: checked ? '#ffffff' : '#991b1b' }]}>{b.bundle_index}</Text>
                </View>
              );
            })}
          </View>
        ) : (
          <Text style={s.noDataText}>No bundle data recorded.</Text>
        )}

        <PageFooter />
      </Page>

      {/* ═══ PAGE 5 — Photos ═══ */}
      {(stuffingPhotos.length > 0 || additionalPhotos.length > 0) && (
        <Page size="A4" style={s.page}>
          <PageTop />
          {stuffingPhotos.length > 0 && (
            <>
              <SectionBar title="STUFFING IMAGES" />
              <View style={s.photoGrid}>
                {stuffingPhotos.map((p, i) => (
                  <View key={i} style={s.photoTile}>
                    <Text style={s.photoLabel}>{p.label || 'Stuffing Image'}</Text>
                    <Image src={p.image_url!} style={s.photoImg} />
                  </View>
                ))}
              </View>
            </>
          )}
          {additionalPhotos.length > 0 && (
            <>
              <SubBar title="ADDITIONAL IMAGES" />
              <View style={s.photoGrid}>
                {additionalPhotos.map((p, i) => (
                  <View key={i} style={s.photoTile}>
                    <Text style={s.photoLabel}>{p.label || 'Additional Image'}</Text>
                    <Image src={p.image_url!} style={s.photoImg} />
                  </View>
                ))}
              </View>
            </>
          )}
          <PageFooter />
        </Page>
      )}

      {/* ═══ REMARKS + SIGNATURES (final page) ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />
        <SectionBar title="NOTES & SIGNATURES" />

        {inspection.remarks && (
          <View style={s.remarksBlock}>
            <Text style={s.remarksLabel}>Additional Notes / Remarks</Text>
            <Text style={s.remarksText}>{inspection.remarks}</Text>
          </View>
        )}

        <View style={s.sigRow}>
          <View style={s.sigBox}>
            <Text style={s.sigLabel}>Factory Rep Signature</Text>
            {inspection.factory_rep_signature_url ? (
              <Image src={inspection.factory_rep_signature_url} style={s.sigImg} />
            ) : (
              <View style={s.sigEmpty} />
            )}
            <Text style={s.sigName}>Authorized Signature</Text>
            <Text style={s.sigRole}>Factory Representative</Text>
          </View>
          <View style={s.sigBox}>
            <Text style={s.sigLabel}>Inspector Signature</Text>
            {inspection.inspector_signature_url ? (
              <Image src={inspection.inspector_signature_url} style={s.sigImg} />
            ) : (
              <View style={s.sigEmpty} />
            )}
            <Text style={s.sigName}>{dash(inspection.inspector_name)}</Text>
            <Text style={s.sigRole}>QA Inspector</Text>
          </View>
          <View style={[s.sigBox, { flex: 0.7 }]}>
            <Text style={s.sigLabel}>Inspection Date</Text>
            <View style={{ height: 44, marginBottom: 4, justifyContent: 'flex-end' }}>
              <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold' }}>
                {formatDate(inspection.created_at)}
              </Text>
            </View>
            <Text style={s.sigRole}>Date of Inspection</Text>
          </View>
        </View>

        <View style={{
          position: 'absolute', bottom: 14, left: 36, right: 36,
          flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
          borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
        }}>
          <Text style={{ fontSize: 7.5, color: '#555555', flex: 1, textAlign: 'center' }}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#2563eb' }}>QAMATE</Text>
            <Text style={{ fontSize: 7.5, color: '#555555' }}>info@qamate.in | www.qamate.in</Text>
          </View>
        </View>
      </Page>

    </Document>
  );
};

export default ContainerInspectionReportPDF;
